import { useEffect, useMemo, useState } from "react";
import { apiGet, apiSend } from "@/lib/api";

type AdminUser = {
  id: string;
  email: string;
  fullName: string;
  role: string;
};

type LoginResponse = {
  token: string;
  user: AdminUser;
};

type DashboardData = {
  metrics: {
    totalProducts: number;
    totalOrders: number;
    pendingOrders: number;
    totalRevenue: number;
    totalStores: number;
  };
  recentOrders: Array<{
    id: string;
    customerName: string;
    status: string;
    totalAmount: number;
    paymentStatus: string;
    createdAt: string;
  }>;
  lowStock: Array<{
    id: string;
    name: string;
    stock: number;
    categoryName: string;
  }>;
};

type ProductItem = {
  id: string;
  name: string;
  sku: string;
  brand: string;
  categoryName: string;
  stock: number;
  price: number;
  salePrice: number | null;
};

const ADMIN_TOKEN_KEY = "kenstore_admin_token";
const ADMIN_USER_KEY = "kenstore_admin_user";

function formatMoney(amount: number): string {
  return `KSh ${Number(amount || 0).toLocaleString("en-KE")}`;
}

export default function AdminPage() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(ADMIN_TOKEN_KEY));
  const [user, setUser] = useState<AdminUser | null>(() => {
    const raw = localStorage.getItem(ADMIN_USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  });

  const [email, setEmail] = useState("admin@kenstore.co.ke");
  const [password, setPassword] = useState("Mochamam@2");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [createName, setCreateName] = useState("");
  const [createPrice, setCreatePrice] = useState("");
  const [createStock, setCreateStock] = useState("20");
  const [createBrand, setCreateBrand] = useState("KenStore");
  const [creating, setCreating] = useState(false);

  const isAuthenticated = Boolean(token && user);

  const metrics = useMemo(() => {
    if (!dashboard) {
      return [
        { label: "Products", value: "-", icon: "ri-box-3-line" },
        { label: "Orders", value: "-", icon: "ri-shopping-bag-3-line" },
        { label: "Pending", value: "-", icon: "ri-time-line" },
        { label: "Revenue", value: "-", icon: "ri-line-chart-line" },
      ];
    }

    return [
      { label: "Products", value: String(dashboard.metrics.totalProducts), icon: "ri-box-3-line" },
      { label: "Orders", value: String(dashboard.metrics.totalOrders), icon: "ri-shopping-bag-3-line" },
      { label: "Pending", value: String(dashboard.metrics.pendingOrders), icon: "ri-time-line" },
      { label: "Revenue", value: formatMoney(dashboard.metrics.totalRevenue), icon: "ri-line-chart-line" },
    ];
  }, [dashboard]);

  async function loadAdminData(activeToken: string) {
    setLoadingData(true);
    setLoadError("");
    try {
      const [dashboardData, productData] = await Promise.all([
        apiGet<DashboardData>("/admin/dashboard", activeToken),
        apiGet<ProductItem[]>("/admin/products", activeToken),
      ]);
      setDashboard(dashboardData);
      setProducts(productData);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load admin data");
    } finally {
      setLoadingData(false);
    }
  }

  useEffect(() => {
    if (token) {
      loadAdminData(token);
    }
  }, [token]);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");

    try {
      const result = await apiSend<LoginResponse>("/auth/admin/login", "POST", { email, password });
      setToken(result.token);
      setUser(result.user);
      localStorage.setItem(ADMIN_TOKEN_KEY, result.token);
      localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(result.user));
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoggingIn(false);
    }
  }

  async function handleCreateProduct(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;

    setCreating(true);
    try {
      await apiSend(
        "/admin/products",
        "POST",
        {
          name: createName,
          price: Number(createPrice),
          stock: Number(createStock),
          brand: createBrand,
          categoryId: "2",
          categoryName: "Phones & Tablets",
          storeName: createBrand,
          images: [],
          tags: [],
        },
        token,
      );
      setCreateName("");
      setCreatePrice("");
      setCreateStock("20");
      await loadAdminData(token);
    } finally {
      setCreating(false);
    }
  }

  function handleLogout() {
    setToken(null);
    setUser(null);
    setDashboard(null);
    setProducts([]);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background-100 via-background-50 to-primary-100 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 bg-background-50 border border-background-200 rounded-3xl overflow-hidden shadow-2xl">
          <div className="p-10 bg-primary-600 text-background-50 relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-primary-400/30" />
            <div className="absolute -left-20 -bottom-20 w-64 h-64 rounded-full bg-accent-400/30" />
            <div className="relative z-10">
              <p className="text-xs uppercase tracking-widest text-background-100/90 mb-3">KenStore 2026</p>
              <h1 className="text-3xl font-heading font-black leading-tight">Admin Control Center</h1>
              <p className="text-sm mt-4 text-background-100/90">
                Manage inventory, monitor orders, and run daily operations for admin.kenstore.co.ke.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-background-100">
                <li className="flex items-center gap-2"><i className="ri-check-line" /> Product lifecycle management</li>
                <li className="flex items-center gap-2"><i className="ri-check-line" /> Real-time order summary</li>
                <li className="flex items-center gap-2"><i className="ri-check-line" /> Low-stock monitoring</li>
              </ul>
            </div>
          </div>

          <div className="p-10">
            <h2 className="text-2xl font-bold text-foreground-950 mb-2">Admin Login</h2>
            <p className="text-sm text-foreground-600 mb-8">Use your admin credentials to access the dashboard.</p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-background-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-background-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  required
                />
              </div>

              {loginError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{loginError}</div>
              )}

              <button
                type="submit"
                disabled={loggingIn}
                className="w-full rounded-xl bg-primary-500 text-background-50 py-3 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60"
              >
                {loggingIn ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100 flex">
      <aside className="hidden lg:flex w-64 bg-foreground-950 text-background-50 flex-col p-5 gap-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-background-300">KenStore Admin</p>
          <h2 className="text-2xl font-heading font-bold mt-2">admin.kenstore.co.ke</h2>
        </div>
        <nav className="space-y-1 text-sm">
          <a href="#" className="block px-3 py-2 rounded-lg bg-background-50/10">Dashboard</a>
          <a href="#" className="block px-3 py-2 rounded-lg hover:bg-background-50/10">Products</a>
          <a href="#" className="block px-3 py-2 rounded-lg hover:bg-background-50/10">Orders</a>
          <a href="#" className="block px-3 py-2 rounded-lg hover:bg-background-50/10">Customers</a>
          <a href="#" className="block px-3 py-2 rounded-lg hover:bg-background-50/10">Settings</a>
        </nav>
        <div className="mt-auto text-xs text-background-300">{user?.email}</div>
      </aside>

      <main className="flex-1 p-4 md:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-black font-heading text-foreground-950">Operations Dashboard</h1>
            <p className="text-sm text-foreground-600 mt-1">Welcome back, {user?.fullName}.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => token && loadAdminData(token)}
              className="px-4 py-2 rounded-lg bg-background-50 border border-background-300 text-sm font-medium"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg bg-foreground-950 text-background-50 text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError}</div>
        )}

        <section className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-8">
          {metrics.map((m) => (
            <article key={m.label} className="rounded-2xl bg-background-50 border border-background-200 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-foreground-500">{m.label}</p>
                <i className={`${m.icon} text-lg text-primary-600`} />
              </div>
              <p className="text-xl md:text-2xl font-black text-foreground-950 mt-3">{m.value}</p>
            </article>
          ))}
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
          <article className="xl:col-span-2 rounded-2xl bg-background-50 border border-background-200 p-4 overflow-x-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-foreground-950">Products</h3>
              <span className="text-xs text-foreground-500">{products.length} total</span>
            </div>
            {loadingData ? (
              <p className="text-sm text-foreground-500 py-5">Loading products...</p>
            ) : (
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr className="text-left text-foreground-500 border-b border-background-200">
                    <th className="py-2">Name</th>
                    <th className="py-2">Brand</th>
                    <th className="py-2">Category</th>
                    <th className="py-2">Stock</th>
                    <th className="py-2">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {products.slice(0, 10).map((p) => (
                    <tr key={p.id} className="border-b border-background-100">
                      <td className="py-2 font-medium text-foreground-900">{p.name}</td>
                      <td className="py-2">{p.brand}</td>
                      <td className="py-2">{p.categoryName}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${p.stock < 25 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                          {p.stock}
                        </span>
                      </td>
                      <td className="py-2">{formatMoney(p.salePrice ?? p.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </article>

          <article className="rounded-2xl bg-background-50 border border-background-200 p-4">
            <h3 className="font-bold text-foreground-950 mb-3">Create Product</h3>
            <form onSubmit={handleCreateProduct} className="space-y-3">
              <input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Product name"
                className="w-full rounded-lg border border-background-300 px-3 py-2 text-sm"
                required
              />
              <input
                value={createBrand}
                onChange={(e) => setCreateBrand(e.target.value)}
                placeholder="Brand"
                className="w-full rounded-lg border border-background-300 px-3 py-2 text-sm"
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={createPrice}
                  onChange={(e) => setCreatePrice(e.target.value)}
                  placeholder="Price"
                  type="number"
                  min="1"
                  className="w-full rounded-lg border border-background-300 px-3 py-2 text-sm"
                  required
                />
                <input
                  value={createStock}
                  onChange={(e) => setCreateStock(e.target.value)}
                  placeholder="Stock"
                  type="number"
                  min="0"
                  className="w-full rounded-lg border border-background-300 px-3 py-2 text-sm"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={creating}
                className="w-full rounded-lg bg-primary-500 text-background-50 py-2 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60"
              >
                {creating ? "Creating..." : "Create Product"}
              </button>
            </form>

            <div className="mt-6">
              <h4 className="text-sm font-semibold text-foreground-900 mb-2">Low Stock</h4>
              <div className="space-y-2 text-sm">
                {(dashboard?.lowStock || []).slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center justify-between rounded-lg bg-background-100 px-3 py-2">
                    <span className="line-clamp-1 pr-3">{item.name}</span>
                    <span className="text-red-700 font-semibold">{item.stock}</span>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </section>

        <section className="rounded-2xl bg-background-50 border border-background-200 p-4">
          <h3 className="font-bold text-foreground-950 mb-3">Recent Orders</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {(dashboard?.recentOrders || []).map((o) => (
              <article key={o.id} className="rounded-xl border border-background-200 bg-background-50 p-3">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-foreground-900">{o.id}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-700">{o.status}</span>
                </div>
                <p className="text-sm text-foreground-700 mt-1">{o.customerName}</p>
                <p className="text-sm text-primary-700 font-semibold mt-2">{formatMoney(o.totalAmount)}</p>
                <p className="text-xs text-foreground-500 mt-1">Payment: {o.paymentStatus}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
